import { useQuery } from '@tanstack/react-query';
import { ExternalLink, SearchX } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { Link, NavLink, Outlet, useLocation, useOutletContext, useParams } from 'react-router';
import { AppMark } from '@/components/layout/app-switcher';
import { APP_NAV } from '@/components/layout/nav';
import { CopyButton } from '@/components/shared/copy-button';
import { EmptyState } from '@/components/shared/empty-state';
import { Spinner } from '@/components/shared/loading';
import { PageHeader } from '@/components/shared/page-header';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api';
import type { AppDetail } from '@/lib/types';
import { cn } from '@/lib/utils';

export type AppCtx = { app: AppDetail };
export const useAppCtx = () => useOutletContext<AppCtx>();


export function AppLayout() {
  const { appId } = useParams();
  const q = useQuery({ queryKey: ['app', appId], queryFn: () => api<AppDetail>(`/admin/v1/apps/${appId}`) });
  const tabsRef = useRef<HTMLElement>(null);
  const { pathname } = useLocation();
  // On narrow screens the tab bar scrolls sideways; keep the active tab visible. Only the bar
  // scrolls (scrollIntoView would also move the page).
  useEffect(() => {
    const bar = tabsRef.current;
    const active = bar?.querySelector<HTMLElement>('[aria-current="page"]');
    if (!bar || !active) return;
    const left = active.offsetLeft - bar.offsetLeft;
    if (left < bar.scrollLeft || left + active.offsetWidth > bar.scrollLeft + bar.clientWidth) bar.scrollTo({ left: left - 8 });
  }, [pathname, q.data]);

  if (q.isLoading) return <Spinner />;
  // The reason is already in a toast (QueryCache onError); the page only offers a way back.
  if (q.error || !q.data) {
    return <EmptyState icon={SearchX} title="This application couldn't be loaded" hint="It may have been deleted, or the API is not reachable." action={<Button asChild variant="outline" size="sm"><Link to="/">Back to applications</Link></Button>} />;
  }
  const app = q.data;

  return (
    <div className="space-y-6">
      {/* The app header and its tabs stay pinned under the top bar. On phones the header wraps
          to several lines, so only the tabs are pinned there; `contents` lets them stick past
          this wrapper (so the gap below the tabs lives on the tab bar, not on it). */}
      <div className="contents space-y-4 md:sticky md:z-[5] md:block md:top-14 md:-mx-6 md:-mt-6 md:bg-background md:px-6 md:pt-6">
        <PageHeader
          title={<span className="flex items-center gap-3"><AppMark name={app.name} logo={app.logo} className="size-9 text-base" />{app.name}</span>}
          description={
            <span className="inline-flex flex-wrap items-center gap-x-3 gap-y-1">
              <span className="inline-flex items-center gap-2 font-mono text-xs">{app.slug} · {app.id} <CopyButton value={app.id} label={false} /></span>
              {app.url && (
                <a href={app.url} target="_blank" rel="noopener noreferrer" className="inline-flex max-w-full items-center gap-1 text-xs text-primary hover:underline">
                  <span className="truncate">{app.url.replace(/^https?:\/\//, '')}</span><ExternalLink className="size-3 shrink-0" />
                </a>
              )}
            </span>
          }
        />
        {/* Styled like the shadcn Tabs list, but made of links so each section keeps its own URL. */}
        <div className="sticky top-14 z-[5] -mx-4 mb-4 bg-background px-4 py-2 md:static md:mx-0 md:mb-0 md:px-0 md:pt-0 md:pb-3">
          <nav ref={tabsRef} aria-label="Application sections" className="flex h-9 w-fit max-w-full items-center gap-0.5 overflow-x-auto rounded-lg bg-muted p-[3px] text-muted-foreground [scrollbar-width:none]">
            {APP_NAV.map((n) => (
              <NavLink
                key={n.to}
                end={n.to === ''}
                to={n.to ? `/apps/${app.id}/${n.to}` : `/apps/${app.id}`}
                className={({ isActive }) => cn(
                  'inline-flex h-full shrink-0 items-center gap-1.5 rounded-md border border-transparent px-3 text-sm font-medium whitespace-nowrap transition-colors outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50',
                  isActive ? 'bg-background text-foreground shadow-sm dark:border-input dark:bg-input/30' : 'hover:text-foreground',
                )}
              >
                <n.icon className="size-4" /> {n.label}
              </NavLink>
            ))}
          </nav>
        </div>
      </div>
      <Outlet context={{ app } satisfies AppCtx} />
    </div>
  );
}
